
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import MainLayout from '@/components/MainLayout';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Search, Award, Calendar, User, Book } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const CertificateVerify = () => {
  const { hash } = useParams<{ hash?: string }>();
  const [searchInput, setSearchInput] = useState(hash || '');
  const [searchQuery, setSearchQuery] = useState(hash || '');

  const { data: certificate, isLoading } = useQuery({
    queryKey: ['verify-certificate', searchQuery],
    queryFn: async () => {
      if (!searchQuery) return null;

      // Try by verification hash first
      let { data, error } = await supabase
        .from('certificates')
        .select('*')
        .eq('verification_hash', searchQuery)
        .maybeSingle();

      // If not found, try by certificate number
      if (!data) {
        const result = await supabase
          .from('certificates')
          .select('*')
          .eq('certificate_number', searchQuery)
          .maybeSingle();
        data = result.data;
        error = result.error;
      }

      if (error) throw error;
      return data;
    },
    enabled: !!searchQuery
  });

  const handleSearch = () => {
    setSearchQuery(searchInput);
  };

  const verificationUrl = certificate 
    ? `${window.location.origin}/verify/${certificate.verification_hash}`
    : '';

  return (
    <MainLayout>
      <div className="container mx-auto py-8 max-w-4xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Certificate Verification</h1>
          <p className="text-muted-foreground">
            Verify the authenticity of learning certificates issued by our platform
          </p>
        </div>

        {/* Search Section */}
        <Card className="p-6 mb-8">
          <div className="flex gap-2">
            <Input
              placeholder="Enter certificate number or verification hash..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button onClick={handleSearch} disabled={isLoading}>
              <Search className="h-4 w-4 mr-2" />
              Verify
            </Button>
          </div>
        </Card>

        {/* Certificate Display */}
        {isLoading && (
          <Card className="p-8">
            <div className="text-center text-muted-foreground">
              Verifying certificate...
            </div>
          </Card>
        )}

        {!isLoading && searchQuery && !certificate && (
          <Card className="p-8 border-destructive">
            <div className="text-center">
              <div className="text-destructive text-5xl mb-4">⚠️</div>
              <h3 className="text-xl font-bold mb-2">Certificate Not Found</h3>
              <p className="text-muted-foreground">
                The certificate number or verification hash you entered could not be found.
                Please check the details and try again.
              </p>
            </div>
          </Card>
        )}

        {certificate && (
          <Card className="p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">✓ Verified Certificate</h2>
              <Badge variant="default" className="text-sm">
                Authentic & Valid
              </Badge>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Certificate Details */}
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Recipient</p>
                    <p className="font-semibold">{certificate.user_name}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Book className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Course</p>
                    <p className="font-semibold">{certificate.course_title}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Award className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Certificate Number</p>
                    <p className="font-mono text-sm">{certificate.certificate_number}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Issue Date</p>
                    <p className="font-semibold">
                      {new Date(certificate.issue_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                {certificate.metadata && (certificate.metadata as any).quiz_average && (
                  <div className="flex items-start gap-3">
                    <div className="h-5 w-5 text-muted-foreground mt-0.5">📊</div>
                    <div>
                      <p className="text-sm text-muted-foreground">Quiz Average</p>
                      <p className="font-semibold">{(certificate.metadata as any).quiz_average}%</p>
                    </div>
                  </div>
                )}
              </div>

              {/* QR Code */}
              <div className="flex flex-col items-center justify-center">
                <div className="bg-white p-4 rounded-lg border-2 border-border">
                  <QRCodeSVG 
                    value={verificationUrl}
                    size={180}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Scan to verify
                </p>
              </div>
            </div>

            {/* Additional Info */}
            <div className="mt-8 pt-6 border-t">
              <p className="text-sm text-muted-foreground text-center">
                This certificate has been verified against our records and is authentic.
                <br />
                Verification Hash: <span className="font-mono text-xs">{certificate.verification_hash?.substring(0, 16)}...</span>
              </p>
            </div>

            {certificate.pdf_url && (
              <div className="mt-4 text-center">
                <Button asChild>
                  <a href={certificate.pdf_url} target="_blank" rel="noopener noreferrer">
                    Download Certificate PDF
                  </a>
                </Button>
              </div>
            )}
          </Card>
        )}

        {/* Info Section */}
        {!searchQuery && (
          <Card className="p-6 bg-muted/50">
            <h3 className="font-semibold mb-3">How to Verify</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>Enter the certificate number found on the certificate</li>
              <li>Alternatively, scan the QR code on the certificate</li>
              <li>View the verified details and authenticity confirmation</li>
            </ol>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default CertificateVerify;
